import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { fetchWrapper as fetch } from '@backrs_ui/grow-ui/src/utils/fetch/fetchWrapper';

import { handlePending, handleRejected } from '../reducers';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

const ENVIRONMENT_URL = process.env.REACT_APP_API_BASEURL;

export const FEATURE_FLAGS = {
  GROW_PAYWALL: 'grow_paywall',
  SHARED_SESSION_GRANTS: 'shared_session_grants',
  FAMILY_ACCOUNTS: 'family_accounts',
  COACHING: 'coaching',
  OAUTH_SHOW_GROW_UI_CONTROLS: 'oauth_show_grow_ui_controls',
  TEST_BOOSTER: 'test_booster',
} as const;

export type FeatureFlags = keyof typeof FEATURE_FLAGS;

/** The flag key as the flag service names it — the values of `FEATURE_FLAGS`, not its keys. */
export type FeatureFlagName = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// Types for the API response
export type FeatureFlagValues = Readonly<Record<string, boolean>>;

type FeatureFlagState = Readonly<{
  flagValues: FeatureFlagValues;
  loading: boolean;
  error?: string;
  /**
   * Whether the flag set is known. `flagValues` is empty both before the first fetch and
   * after one fails, so consumers gating on it alone can't tell "not yet" from "not on".
   */
  settled: boolean;
}>;

const initialState: FeatureFlagState = {
  flagValues: {},
  loading: false,
  settled: false,
};

// Async thunk to fetch feature flag values
export const fetchFeatureFlags = createAsyncThunk(
  'featureFlags/fetchFeatureFlags',
  async (_, { rejectWithValue }) => {
    try {
      if (!ENVIRONMENT_URL) {
        return rejectWithValue('No environment URL found');
      }

      const response = await fetch(`${ENVIRONMENT_URL}/feature-flags/flags`);

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      return data as FeatureFlagValues;
    } catch (error) {
      return rejectWithValue(`Failed to fetch feature flags: ${error}`);
    }
  },
);

export const featureFlagSlice = createSlice({
  name: 'featureFlags',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFeatureFlags.pending, (state) => {
        handlePending(state);
        // A refetch un-settles the set: `logout` settles it while emptying `flagValues`, so
        // without this a re-login would read every flag as off for the whole in-flight window.
        state.settled = false;
      })
      .addCase(fetchFeatureFlags.rejected, (state, action) => {
        handleRejected(state, action);
        // A failed fetch is still an answer: no flags are on.
        state.settled = true;
      })
      .addCase(fetchFeatureFlags.fulfilled, (state, action: PayloadAction<FeatureFlagValues>) => {
        state.loading = false;
        state.flagValues = action.payload;
        state.settled = true;
      })
      // LOGOUT - Clear feature flags when user logs out
      .addCase(logout, (state) => {
        state.flagValues = {};
        state.loading = false;
        state.error = undefined;
        // A logged-out visitor has no flags; that is known, not pending.
        state.settled = true;
      });
  },
});

export const selectIsFeatureFlagEnabled = (flagName: string) => (state: RootState) =>
  state.featureFlags.flagValues[flagName] || false;
